/**
 * HR Admin Panel — Digi Sahyadri WRMS
 * Full control: all requests, manual add, override, leave balance management, export
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Modal, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaSearch, FaFilter, FaPlus, FaFileExport, FaEdit, FaExternalLinkAlt, FaSync, FaThLarge, FaList, FaCalendarAlt } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import StatusBadge from '../shared/StatusBadge';
import RequestCard from '../shared/RequestCard';
import { getAllRequests, overrideRequest, manualAddRequest, getRequestTypeLabel, isApprovedRequestActionableByFromDate } from '../../services/wrmsService';
import { getEmployees } from '../../../services/firebase/employeeService';

const TYPE_FILTER = ['', 'leave', 'early_leave', 'gate_pass', 'extra_work'];
const TYPE_LABELS = { '': 'All Types', leave: 'Leave', early_leave: 'Early Leave', gate_pass: 'Gate Pass', extra_work: 'Extra Work' };
const STATUS_FILTER = ['', 'pending', 'approved', 'on_hold', 'rejected'];

const HRAdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(location.state?.typeFilter || '');
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');

  // Date Filters & Layout States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [layoutMode, setLayoutMode] = useState('list');

  // Override modal
  const [overrideModal, setOverrideModal] = useState({ show: false, request: null, newStatus: '' });
  const [overrideReason, setOverrideReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, emps] = await Promise.all([
        getAllRequests({ requestType: typeFilter || null, status: statusFilter || null }),
        getEmployees(),
      ]);
      setRequests(reqs);
      setEmployees(emps?.data || emps || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredRequests = requests.filter(req => {
    const matchSearch = !search ||
      req.employeeName?.toLowerCase().includes(search.toLowerCase()) ||
      req.teamId?.toLowerCase().includes(search.toLowerCase()) ||
      req.requestId?.toLowerCase().includes(search.toLowerCase()) ||
      (req.reason && req.reason.toLowerCase().includes(search.toLowerCase())) ||
      (req.description && req.description.toLowerCase().includes(search.toLowerCase()));
    const matchDept = !deptFilter || req.department?.toLowerCase().includes(deptFilter.toLowerCase());

    let matchDate = true;
    if (startDate || endDate) {
      const reqDate = req.submittedAt?.toDate ? req.submittedAt.toDate() : new Date(req.submittedAt);
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (reqDate < start) matchDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (reqDate > end) matchDate = false;
      }
    }
    return matchSearch && matchDept && matchDate;
  });

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDateRange = (r) => {
    if (!r) return '—';
    if (r.requestType === 'leave') return `${formatDate(r.fromDate)} → ${formatDate(r.toDate)}`;
    if (r.requestType === 'early_leave') return formatDate(r.fromDate);
    if (r.requestType === 'gate_pass') return `${formatDate(r.date)} → ${formatDate(r.returnDate)}`;
    if (r.requestType === 'extra_work') return formatDate(r.workDate);
    return formatDate(r.fromDate || r.date || r.workDate);
  };

  // Export CSV
  const exportCSV = () => {
    const rows = filteredRequests.map(r => ({
      'Request ID': r.requestId,
      'Type': getRequestTypeLabel(r.requestType),
      'Employee': r.employeeName,
      'Team ID': r.teamId,
      'Department': r.department,
      'Designation': r.designation,
      'Status': r.status,
      'Current Stage': r.currentStage,
      'Submitted': formatDate(r.submittedAt),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Requests');
    XLSX.writeFile(wb, `Requests_Report_${Date.now()}.xlsx`);
    toast.success('Exported to Excel');
  };

  // Export PDF
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('Requests Report — Digi Sahyadri', 14, 16);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    autoTable(doc, {
      startY: 28,
      head: [['Request ID', 'Type', 'Employee', 'Team ID', 'Department', 'Status', 'Stage', 'Submitted']],
      body: filteredRequests.map(r => [
        r.requestId, getRequestTypeLabel(r.requestType), r.employeeName,
        r.teamId, r.department, r.status, r.currentStage, formatDate(r.submittedAt)
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255 },
    });

    doc.save(`Requests_Report_${Date.now()}.pdf`);
    toast.success('Exported to PDF');
  };

  // Override
  const handleOverrideConfirm = async () => {
    if (!overrideReason.trim()) { toast.error('Reason is required for override'); return; }
    setProcessing(true);
    try {
      await overrideRequest(
        overrideModal.request.requestType,
        overrideModal.request.id,
        overrideModal.newStatus,
        user,
        overrideReason
      );
      toast.success(`Request ${overrideModal.newStatus} by admin`);
      setOverrideModal({ show: false, request: null, newStatus: '' });
      setOverrideReason('');
      fetchAll();
    } catch (err) {
      toast.error('Override failed');
    } finally {
      setProcessing(false);
    }
  };



  return (
    <div className="wrms-admin-panel-root" style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* Responsive Filter Bar */}
      <div className="wrms-admin-filter-bar">
        <div className="wrms-admin-search-wrap">
          <FaSearch className="wrms-search-icon" />
          <input
            className="wrms-form-control form-control"
            placeholder="Search by name, Team ID, Request ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="wrms-admin-select-row">
          <select className="wrms-form-control form-control form-select"
            value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            {TYPE_FILTER.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
          <select className="wrms-form-control form-control form-select"
            value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            {STATUS_FILTER.map(s => <option key={s} value={s}>{s ? (s === 'on_hold' ? 'On Hold' : s.charAt(0).toUpperCase() + s.slice(1)) : 'All Status'}</option>)}
          </select>
        </div>
        <div className="wrms-admin-dept-wrap">
          <input className="wrms-form-control form-control" placeholder="Filter by Dept..."
            value={deptFilter} onChange={e => setDeptFilter(e.target.value)} />
        </div>

        {/* Date range filters */}
        <div className="wrms-admin-dates-row">
          <div className="wrms-date-field">
            <span className="wrms-date-label">From</span>
            <input type="date" className="wrms-form-control form-control"
              value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="wrms-date-field">
            <span className="wrms-date-label">To</span>
            <input type="date" className="wrms-form-control form-control"
              value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        {/* Action buttons */}
        <div className="wrms-admin-actions-row">
          <button className="wrms-btn-outline" onClick={fetchAll} title="Refresh requests">
            <FaSync size={11} />
          </button>
          <button className="wrms-btn-primary" onClick={exportCSV}>
            <FaFileExport size={12} /> CSV
          </button>
          <button className="wrms-btn-primary" onClick={exportPDF} style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
            <FaFileExport size={12} /> PDF
          </button>

          {/* Layout switcher */}
          <div className="wrms-view-toggle">
            <button
              className={layoutMode === 'list' ? 'active' : ''}
              onClick={() => setLayoutMode('list')}
              title="Table View (List)"
            >
              <FaList size={13} />
            </button>
            <button
              className={layoutMode === 'grid' ? 'active' : ''}
              onClick={() => setLayoutMode('grid')}
              title="Grid View"
            >
              <FaThLarge size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Count */}
      <div style={{ marginBottom: '16px', fontSize: '0.8rem', color: '#64748b' }}>
        Showing <strong>{filteredRequests.length}</strong> of <strong>{requests.length}</strong> requests
      </div>

      {/* Requests Rendering */}
      {layoutMode === 'list' ? (
        <div className="wrms-table">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Employee</th>
                <th>Type</th>
                <th>Department</th>
                <th>Status</th>
                <th>Stage</th>
                <th>Leave / Request Dates</th>
                <th>Submitted</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <div className="spinner-border text-primary" role="status" />
                  <p style={{ marginTop: '12px' }}>Loading all requests...</p>
                </td></tr>
              ) : filteredRequests.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  No requests found matching your filters.
                </td></tr>
              ) : filteredRequests.map(req => (
                <tr key={req.id}>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#4f46e5', fontWeight: 700 }}>
                      {req.requestId}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0f172a' }}>{req.employeeName}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{req.teamId}</div>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                      {getRequestTypeLabel(req.requestType)}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{req.department}</td>
                  <td><StatusBadge status={req.status} /></td>
                  <td style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'capitalize' }}>
                    {req.currentStage?.replace(/_/g, ' ')}
                  </td>
                  <td style={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                    {getDateRange(req)}
                  </td>
                  <td style={{ fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap' }}>{formatDate(req.submittedAt)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button className="wrms-btn-outline"
                        onClick={() => navigate(`/wrms/request/${req.requestType}/${req.id}`)}
                        style={{ padding: '4px 10px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FaExternalLinkAlt size={9} /> View
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button className="wrms-btn-success"
                            onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'approved' }); setOverrideReason(''); }}
                            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>
                            Approve
                          </button>
                          <button className="wrms-btn-warning"
                            onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'on_hold' }); setOverrideReason(''); }}
                            style={{ padding: '4px 10px', fontSize: '0.72rem', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>
                            Hold
                          </button>
                          <button className="wrms-btn-danger"
                            onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'rejected' }); setOverrideReason(''); }}
                            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>
                            Reject
                          </button>
                        </>
                      )}
                      {req.status === 'approved' && (
                        isApprovedRequestActionableByFromDate(req) ? (
                          <>
                            <button className="wrms-btn-warning"
                              onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'on_hold' }); setOverrideReason(''); }}
                              style={{ padding: '4px 10px', fontSize: '0.72rem', background: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}>
                              Hold
                            </button>
                            <button className="wrms-btn-danger"
                              onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'rejected' }); setOverrideReason(''); }}
                              style={{ padding: '4px 10px', fontSize: '0.72rem' }}>
                              Reject
                            </button>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic' }}>
                            Locked (Started)
                          </span>
                        )
                      )}
                      {req.status === 'on_hold' && (
                        <>
                          <button className="wrms-btn-success"
                            onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'approved' }); setOverrideReason(''); }}
                            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>
                            Re-Approve
                          </button>
                          <button className="wrms-btn-danger"
                            onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'rejected' }); setOverrideReason(''); }}
                            style={{ padding: '4px 10px', fontSize: '0.72rem' }}>
                            Reject
                          </button>
                        </>
                      )}
                      {req.status === 'rejected' && (
                        <button className="wrms-btn-outline"
                          onClick={() => { setOverrideModal({ show: true, request: req, newStatus: 'approved' }); setOverrideReason(''); }}
                          style={{ padding: '4px 10px', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <FaEdit size={9} /> Override
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="wrms-request-grid">
          {loading ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <div className="spinner-border text-primary" role="status" />
              <p style={{ marginTop: '12px' }}>Loading requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              No requests found matching your filters.
            </div>
          ) : filteredRequests.map(req => (
            <RequestCard
              key={req.id}
              request={req}
              showEmployee={true}
              layout="grid"
              approverActions={
                req.status === 'pending' ? {
                  handleApprove: (r) => setOverrideModal({ show: true, request: r, newStatus: 'approved' }),
                  handleHold: (r) => setOverrideModal({ show: true, request: r, newStatus: 'on_hold' }),
                  handleReject: (r) => setOverrideModal({ show: true, request: r, newStatus: 'rejected' })
                } : req.status === 'approved' ? (
                  isApprovedRequestActionableByFromDate(req) ? {
                    handleHold: (r) => setOverrideModal({ show: true, request: r, newStatus: 'on_hold' }),
                    handleReject: (r) => setOverrideModal({ show: true, request: r, newStatus: 'rejected' })
                  } : null
                ) : req.status === 'on_hold' ? {
                  handleApprove: (r) => setOverrideModal({ show: true, request: r, newStatus: 'approved' }),
                  handleReject: (r) => setOverrideModal({ show: true, request: r, newStatus: 'rejected' })
                } : null
              }
            />
          ))}
        </div>
      )}

      {/* Override Modal */}
      <Modal show={overrideModal.show} onHide={() => setOverrideModal({ show: false, request: null, newStatus: '' })} centered className="wrms-approve-modal">
        <Modal.Header closeButton>
          <Modal.Title style={{
            fontWeight: 800,
            fontSize: '1rem',
            color: overrideModal.newStatus === 'approved' ? '#059669' : overrideModal.newStatus === 'on_hold' ? '#b45309' : '#dc2626'
          }}>
            {overrideModal.newStatus === 'approved'
              ? 'Override: Approve Request'
              : overrideModal.newStatus === 'on_hold'
              ? 'Put Request On Hold'
              : 'Override: Reject Request'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          {overrideModal.request && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px' }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>
                {overrideModal.request.employeeName} — {getRequestTypeLabel(overrideModal.request.requestType)}
              </div>
              <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{overrideModal.request.requestId}</div>
              {overrideModal.request.status === 'approved' && overrideModal.request.requestType === 'leave' && (
                <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#b45309', background: '#fef3c7', padding: '6px 10px', borderRadius: '6px' }}>
                  <strong>Notice:</strong> This leave is currently <strong>Approved</strong>. Changing to {overrideModal.newStatus.replace(/_/g, ' ')} will automatically refund <strong>{overrideModal.request.daysCount} days</strong> back to the employee's balance.
                </div>
              )}
            </div>
          )}
          <Form.Group>
            <Form.Label style={{ fontWeight: 700, fontSize: '0.82rem' }}>
              Reason for Action <span style={{ color: '#dc2626' }}>*</span>
            </Form.Label>
            <Form.Control as="textarea" rows={3}
              placeholder="Reason (required for immutable audit trail)"
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              style={{ borderRadius: '10px', fontSize: '0.875rem' }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #f1f5f9', padding: '16px 24px' }}>
          <button className="wrms-btn-outline" onClick={() => setOverrideModal({ show: false, request: null, newStatus: '' })}
            style={{ padding: '8px 20px' }}>Cancel</button>
          <button
            onClick={handleOverrideConfirm}
            disabled={processing}
            style={{
              padding: '8px 24px', borderRadius: '8px', border: 'none', fontWeight: 700, fontSize: '0.875rem',
              background: overrideModal.newStatus === 'approved' ? '#059669' : overrideModal.newStatus === 'on_hold' ? '#d97706' : '#dc2626',
              color: 'white', cursor: 'pointer',
            }}>
            {processing ? 'Processing...' : `Confirm ${overrideModal.newStatus === 'approved' ? 'Approval' : overrideModal.newStatus === 'on_hold' ? 'Hold' : 'Rejection'}`}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HRAdminPanel;
